import { Link } from "react-router-dom";
import { MapPin, ExternalLink, ChevronRight } from "lucide-react";
import SectionHeading from "../components/SectionHeading";

export default function VeteransPark() {
  return (
    <main className="pt-24">
      {/* Hero */}
      <section className="relative h-[75vh] min-h-[560px] w-full overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1685389213849-088712cbd3fe"
          alt="Memorial"
          className="absolute inset-0 w-full h-full object-cover slow-zoom"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A3161]/60 via-[#0A3161]/40 to-[#0A3161]" />
        <div className="relative h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full text-white fade-up">
            <p className="font-condensed uppercase tracking-[0.3em] text-xs text-[#FFFFFF] mb-4">
              A Place To Remember
            </p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-bold leading-[0.95] max-w-4xl">
              Sumner County
              <br />
              <span className="italic text-[#FFFFFF]">Veterans Park</span>
            </h1>
            <p className="mt-8 text-xl text-white/85 max-w-2xl">
              Designed and maintained by Chapter 240. Memorializing every man and woman from Sumner County who answered the call, from WWI through Iraq.
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-28 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 relative">
            <img
              src="https://images.unsplash.com/photo-1585502094744-8943e25971fa"
              alt="Memorial wall"
              className="w-full h-[560px] object-cover"
            />
            <div className="absolute -bottom-6 -right-6 bg-[#B22234] text-white p-6 max-w-xs">
              <p className="font-display italic text-lg leading-snug">
                Every name is a life. Every brick is a story.
              </p>
            </div>
          </div>
          <div className="lg:col-span-6">
            <SectionHeading
              eyebrow="About The Park"
              title="A Living Tribute In Gallatin"
              description="Located behind the Sumner County Administration Building at 355 Belvedere, Gallatin, TN, the park honors local veterans through a tiered memorial engraved with their names."
            />
            <p className="mt-6 text-[#1F2937] leading-relaxed">
              At the heart of the park stands the Wall of Honor. Here, families and veterans may dedicate an engraved brick honoring service to our country. Together, these bricks form a mosaic of sacrifice that will endure for generations.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <div className="border border-[#D9DDE3] p-6 bg-white">
                <p className="font-condensed uppercase tracking-widest text-xs text-[#B22234]">
                  Address
                </p>
                <p className="mt-2 text-[#0A3161]">355 Belvedere</p>
                <p className="text-[#0A3161]">Gallatin, TN</p>
              </div>
              <div className="border border-[#D9DDE3] p-6 bg-white">
                <p className="font-condensed uppercase tracking-widest text-xs text-[#B22234]">
                  Honors
                </p>
                <p className="mt-2 text-[#0A3161]">WWI to Iraq</p>
                <p className="text-[#0A3161] text-sm">Men & women of Sumner County</p>
              </div>
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="http://sumnercountyveteranspark.com/"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-[#0A3161] text-white px-7 h-12 font-condensed uppercase tracking-widest text-xs hover:bg-[#B22234] transition-colors"
              >
                Visit Park Website <ExternalLink className="w-4 h-4" />
              </a>
              <Link
                to="/membership"
                className="inline-flex items-center gap-2 border border-[#0A3161] text-[#0A3161] hover:bg-[#0A3161] hover:text-white px-7 h-12 font-condensed uppercase tracking-widest text-xs transition-colors"
              >
                Support The Chapter <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Wall of Honor */}
      <section className="relative py-28 px-6 lg:px-10 bg-[#0A3161] overflow-hidden">
        <div className="absolute inset-0 stars opacity-20" />
        <div className="relative max-w-5xl mx-auto text-center">
          <SectionHeading
            align="center"
            light
            eyebrow="Wall Of Honor"
            title="Dedicate An Engraved Brick"
            description="Preserve a legacy of service. Bricks may be engraved with a veteran's name, rank, branch and dates of service. A lasting tribute at the heart of the park."
          />
          <div className="mt-12 grid md:grid-cols-3 gap-6 text-left">
            {[
              { step: "01", title: "Choose Your Brick", text: "Select from available sizes and inscription options." },
              { step: "02", title: "Provide The Story", text: "Share the veteran's name, service branch and years served." },
              { step: "03", title: "See It Set In Stone", text: "Your brick joins the Wall of Honor at the park." },
            ].map((s) => (
              <div key={s.step} className="bg-white/5 border border-white/10 backdrop-blur-sm p-8">
                <p className="font-display text-5xl font-bold text-[#FFFFFF]">{s.step}</p>
                <h3 className="mt-4 font-display text-2xl text-white font-bold">{s.title}</h3>
                <p className="mt-3 text-white/70 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
          <a
            href="http://sumnercountyveteranspark.com/"
            target="_blank"
            rel="noreferrer"
            className="mt-14 inline-flex items-center gap-2 bg-[#B22234] hover:bg-[#8f1b2a] text-white px-8 h-14 font-condensed uppercase tracking-widest text-sm"
          >
            Start Your Dedication <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </section>
    </main>
  );
}
